import { useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import {
  type CompanyFinancialsTableResponse,
  type CompanyFinancialsTrailingPeriod,
  type CompanyFinancialsView,
} from '@/services/companies.service'
import type { UseQueryResult } from '@tanstack/react-query'
import {
  useFinancialsTableState,
  type FinancialsTableOptions,
} from '@/hooks/use-financials-table-state'
import { useCompanyFinancialsTable } from '@/hooks/use-company-financials'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useFinancialsSectionTabs } from '@/hooks/use-financials-section-tabs'
import { useScrollToEndOnColumnChange } from '@/hooks/use-scroll-to-end-on-column-change'
import { filterFinancialStatementRows } from '@/lib/financials-sections'
import {
  FINANCIAL_SCENARIOS,
  filterFinancialRowsForScenario,
  getFinancialSectionsForScenario,
  getFinancialsEmptyState,
  isFinancialRowBlank,
} from '@/lib/financials-scenarios'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { SearchInput } from '@/components/custom/search-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { CellNumber } from '@/components/custom/datatable/cells/cell-number'
import { SectionHeader } from '@/components/custom/companies/section-header'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const METRIC_COLUMN_WIDTH =
  'w-36 min-w-36 max-w-36 @sm/financials:w-55 @sm/financials:min-w-55 @sm/financials:max-w-55'

const HEADER_BG =
  'color-mix(in srgb, var(--primary) 8%, var(--background))' as const

function formatMetaDate(value: string | undefined) {
  if (!value) return '—'
  return formatDate(value)
}

function formatForDataType(dataType: string | undefined) {
  const normalized = dataType?.toLowerCase()
  if (normalized === 'currency') return 'currency'
  if (normalized === 'percentage') return 'percentage'
  return undefined
}

function isView(value: string): value is CompanyFinancialsView {
  return value === 'MONTHLY' || value === 'QUARTERLY' || value === 'ANNUAL'
}

function isTrailingPeriod(
  value: string
): value is CompanyFinancialsTrailingPeriod {
  return (
    value === 'LTM' ||
    value === 'MONTHLY' ||
    value === 'QUARTERLY' ||
    value === 'YTD'
  )
}

function TableOverlay({
  title,
  description,
  buttonText,
  onClickButton,
}: {
  title: string
  description: string
  buttonText?: string
  onClickButton?: () => Promise<unknown> | void
}) {
  const [isLoading, setIsLoading] = useState(false)

  const handleClick = async () => {
    if (!onClickButton) return
    setIsLoading(true)
    try {
      await onClickButton()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Card className="w-[370px] pointer-events-auto rounded-md border border-primary/50 p-8 shadow-[0px_10px_22px_0px_#0000001A,0px_41px_41px_0px_#00000017,0px_92px_55px_0px_#0000000D,0px_163px_65px_0px_#00000003,0px_254px_71px_0px_#00000000]">
      <CardContent className="flex flex-col items-center gap-3 p-0 text-center">
        <Typography variant="h4" className="font-bold">
          {title}
        </Typography>
        <Typography variant="body-sm" className="text-primary/70">
          {description}
        </Typography>
        {onClickButton && buttonText ? (
          <Button
            type="button"
            variant="outline"
            onClick={handleClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              buttonText
            )}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

function NoResultsOverlay({ onClear }: { onClear: () => void }) {
  return (
    <TableOverlay
      title="No matching metrics"
      description="Try a different search term."
      buttonText="Clear search"
      onClickButton={() => onClear()}
    />
  )
}

interface FinancialsTableProps extends FinancialsTableOptions {
  companyId: string
}

export function FinancialsTable({
  companyId,
  ...options
}: FinancialsTableProps) {
  const state = useFinancialsTableState(options)
  const query = useCompanyFinancialsTable(companyId, state.queryParams)
  return (
    <FinancialsTableLayout
      state={state}
      query={query}
      companyId={companyId}
      idPrefix="financial-statement"
    />
  )
}

interface FinancialsTableLayoutProps {
  state: ReturnType<typeof useFinancialsTableState>
  query: Pick<
    UseQueryResult<CompanyFinancialsTableResponse>,
    'data' | 'isLoading' | 'isPlaceholderData' | 'isError' | 'refetch'
  >
  companyId?: string
  idPrefix: string
}

export function FinancialsTableLayout({
  state,
  query,
  companyId,
  idPrefix,
}: FinancialsTableLayoutProps) {
  const {
    scenarioType,
    setScenarioType,
    trailingPeriod,
    setTrailingPeriod,
    showBlankPeriods,
    setShowBlankPeriods,
    view,
    setView,
  } = state
  const [showBlankRows, setShowBlankRows] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 200)
  const { data, isLoading, isPlaceholderData, isError, refetch } = query

  const dataMatchesRequest =
    !isPlaceholderData &&
    (companyId === undefined || data?.meta.companyId === companyId) &&
    data?.meta.scenarioType === scenarioType &&
    data.meta.view === view &&
    data.meta.trailingPeriod === trailingPeriod
  const rows = useMemo(
    () => (dataMatchesRequest ? (data?.rows ?? []) : []),
    [data?.rows, dataMatchesRequest]
  )
  const columns = dataMatchesRequest ? (data?.columns ?? []) : []
  const financialRows = useMemo(
    () =>
      filterFinancialRowsForScenario(
        filterFinancialStatementRows(rows),
        scenarioType
      ),
    [rows, scenarioType]
  )
  const financialSectionNames = useMemo(
    () => getFinancialSectionsForScenario(financialRows, scenarioType),
    [financialRows, scenarioType]
  )

  const scrollContainerRef = useScrollToEndOnColumnChange(columns)

  const {
    availableSections,
    activeSection,
    setUserSelectedSection,
    sectionFilteredRows,
  } = useFinancialsSectionTabs(financialRows, financialSectionNames)

  const placeholderColumns = useMemo(() => {
    const count = view === 'MONTHLY' ? 6 : view === 'QUARTERLY' ? 4 : 3
    return Array.from({ length: count }).map((_, index) => ({
      key: `__placeholder-${index}`,
      label: '',
    }))
  }, [view])

  const renderedColumns = columns.length > 0 ? columns : placeholderColumns

  const filteredRows = useMemo(() => {
    const isBlankRow = (row: (typeof sectionFilteredRows)[number]) =>
      isFinancialRowBlank(row.values, scenarioType)
    const visibleRows = showBlankRows
      ? sectionFilteredRows
      : sectionFilteredRows.filter((row) => !isBlankRow(row))
    const term = debouncedSearch.trim().toLowerCase()
    if (!term) return visibleRows
    return visibleRows.filter((row) => {
      const haystack = [
        row.label,
        row.metricTypeName ?? '',
        row.metricTypeCode ?? '',
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [debouncedSearch, scenarioType, sectionFilteredRows, showBlankRows])

  const meta = dataMatchesRequest ? data?.meta : undefined

  const showTableLoading = isLoading || isPlaceholderData

  const showNoResultsOverlay =
    !showTableLoading &&
    sectionFilteredRows.length > 0 &&
    filteredRows.length === 0 &&
    debouncedSearch.trim() !== ''

  const showAllBlankOverlay =
    !showTableLoading &&
    financialRows.length > 0 &&
    filteredRows.length === 0 &&
    debouncedSearch.trim() === '' &&
    !showBlankRows &&
    sectionFilteredRows.length > 0

  const showEmptyOverlay =
    !showTableLoading &&
    !isError &&
    (scenarioType === 'BUDGET'
      ? sectionFilteredRows.length === 0
      : financialRows.length === 0)
  const showErrorOverlay = Boolean(isError)
  const showAnyOverlay =
    showNoResultsOverlay ||
    showAllBlankOverlay ||
    showEmptyOverlay ||
    showErrorOverlay

  const selectTriggerClassName =
    'h-9 w-full cursor-pointer rounded-sm border-primary/20 bg-white px-3 text-sm font-medium leading-5 shadow-none sm:w-auto [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-60'
  const emptyState = getFinancialsEmptyState(scenarioType, activeSection)
  const scenarioLabel =
    FINANCIAL_SCENARIOS.find(({ value }) => value === scenarioType)?.label ??
    scenarioType

  return (
    <div className="@container/financials space-y-3">
      <SectionHeader
        title="Financial Summary"
        afterTitle={
          <Typography
            variant="body-sm"
            className="cursor-default text-primary/70"
          >
            {scenarioLabel}
            {meta?.asOfPeriodEndDate
              ? ` · As of ${formatMetaDate(meta.asOfPeriodEndDate)}`
              : ''}
          </Typography>
        }
        right={
          meta?.updatedAt ? (
            <Typography
              variant="body-sm"
              className="cursor-default text-primary/70"
            >
              Last synced {formatMetaDate(meta.updatedAt)}
            </Typography>
          ) : null
        }
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="w-full sm:min-w-52 sm:flex-1">
          <SearchInput
            placeholder="Search metrics..."
            value={search}
            onChange={setSearch}
            className="w-full [&>input]:h-9 [&>input]:border-primary/20 [&>input]:pl-7"
          />
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
          <Select
            value={scenarioType}
            onValueChange={(nextScenario) => {
              const scenario = FINANCIAL_SCENARIOS.find(
                ({ value }) => value === nextScenario
              )
              if (scenario) {
                setUserSelectedSection(null)
                setScenarioType(scenario.value)
              }
            }}
          >
            <SelectTrigger
              aria-label="Scenario"
              className={selectTriggerClassName}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="font-normal text-primary/70">Scenario</span>
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent align="end" className="min-w-36">
              {FINANCIAL_SCENARIOS.map((scenario) => (
                <SelectItem key={scenario.value} value={scenario.value}>
                  {scenario.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={trailingPeriod}
            onValueChange={(next) => {
              if (isTrailingPeriod(next)) setTrailingPeriod(next)
            }}
          >
            <SelectTrigger
              aria-label="Trailing financial period"
              className={selectTriggerClassName}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="font-normal text-primary/70">Period</span>
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent align="end" className="min-w-40">
              <SelectItem value="LTM">LTM</SelectItem>
              <SelectItem value="MONTHLY">Month</SelectItem>
              <SelectItem value="QUARTERLY">Quarter</SelectItem>
              <SelectItem value="YTD">YTD</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={view}
            onValueChange={(next) => {
              if (isView(next)) setView(next)
            }}
          >
            <SelectTrigger
              aria-label="Financial column view"
              className={selectTriggerClassName}
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="font-normal text-primary/70">View</span>
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent align="end" className="min-w-40">
              <SelectItem value="MONTHLY">Monthly</SelectItem>
              <SelectItem value="QUARTERLY">Quarterly</SelectItem>
              <SelectItem value="ANNUAL">Yearly</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <label className="flex h-9 shrink-0 items-center gap-2 rounded-sm text-sm leading-5 text-primary/70">
            <Checkbox
              checked={showBlankPeriods}
              onCheckedChange={(checked) =>
                setShowBlankPeriods(checked === true)
              }
            />
            Blank Periods
          </label>

          <label className="flex h-9 shrink-0 items-center gap-2 rounded-sm text-sm leading-5 text-primary/70">
            <Checkbox
              checked={showBlankRows}
              onCheckedChange={(checked) => setShowBlankRows(checked === true)}
            />
            Blank Rows
          </label>
        </div>
      </div>

      {availableSections.length > 0 ? (
        <Tabs
          value={activeSection ?? availableSections[0]}
          onValueChange={setUserSelectedSection}
        >
          <TabsList
            aria-label="Financial statement"
            className="h-auto w-full justify-start overflow-x-auto rounded-sm bg-primary/5 p-1 scrollbar-visible"
          >
            {availableSections.map((section, index) => (
              <TabsTrigger
                key={section}
                id={`${idPrefix}-tab-${index}`}
                aria-controls={`${idPrefix}-tabpanel`}
                value={section}
                className="shrink-0"
              >
                {section}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      ) : null}

      <div
        id={`${idPrefix}-tabpanel`}
        role="tabpanel"
        aria-labelledby={`${idPrefix}-tab-${Math.max(
          availableSections.indexOf(
            activeSection ?? availableSections[0] ?? ''
          ),
          0
        )}`}
        tabIndex={0}
        className="relative overflow-hidden rounded-sm border border-primary/20 bg-white"
      >
        {showTableLoading ? (
          <Typography
            variant="body-sm"
            className="sr-only"
            role="status"
            aria-live="polite"
          >
            Loading {scenarioLabel} financials
          </Typography>
        ) : null}
        <div
          ref={scrollContainerRef}
          className={cn('relative overflow-auto scrollbar-visible')}
          style={{
            height: 'clamp(320px, 45vh, 480px)',
            maxHeight: 'calc(100vh - 260px)',
          }}
        >
          <Table
            disableWrapper
            className="!table-auto [&_th]:border-primary/20 [&_td]:border-primary/20 [&_th:first-child]:border-l-0 [&_td:first-child]:border-l-0 [&_th:last-child]:border-r-0 [&_td:last-child]:border-r-0 [&_tbody_tr:last-child_td]:border-b-0"
          >
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead
                  style={{ background: HEADER_BG }}
                  className={cn(
                    'sticky left-0 top-0 z-30',
                    METRIC_COLUMN_WIDTH
                  )}
                >
                  Metric
                </TableHead>
                {renderedColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    style={{ background: HEADER_BG }}
                    className="sticky top-0 z-20 min-w-[110px]"
                  >
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {showTableLoading || showAnyOverlay ? (
                Array.from({ length: 14 }).map((_, rowIndex) => (
                  <TableRow key={rowIndex} className="hover:bg-transparent">
                    <TableCell
                      className={cn(
                        'sticky left-0 z-10 bg-white',
                        METRIC_COLUMN_WIDTH
                      )}
                    >
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                    {renderedColumns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredRows.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={Math.max(renderedColumns.length + 1, 5)}
                    className="h-16"
                  />
                </TableRow>
              ) : (
                filteredRows.map((row) => (
                  <TableRow key={row.key} className="hover:bg-primary/5">
                    <TableCell
                      className={cn(
                        'sticky left-0 z-10 bg-white',
                        METRIC_COLUMN_WIDTH
                      )}
                    >
                      <Typography
                        variant="body-sm"
                        className="max-w-32 whitespace-normal break-words text-primary/80 @sm/financials:max-w-51"
                      >
                        {row.label}
                      </Typography>
                    </TableCell>
                    {columns.map((col) => (
                      <TableCell key={`${row.key}-${col.key}`}>
                        <CellNumber
                          value={row.values[col.key] ?? null}
                          format={formatForDataType(row.dataType)}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {showNoResultsOverlay ? (
          <div className="absolute inset-0 z-20 flex pointer-events-none items-center justify-center">
            <NoResultsOverlay onClear={() => setSearch('')} />
          </div>
        ) : null}
        {showAllBlankOverlay ? (
          <div className="absolute inset-0 z-20 flex pointer-events-none items-center justify-center">
            <TableOverlay
              title="All metrics in this section are blank"
              description="Every metric in this section has no values for the selected period. Enable Blank Rows to show them."
              buttonText="Show blank rows"
              onClickButton={() => setShowBlankRows(true)}
            />
          </div>
        ) : null}
        {showErrorOverlay ? (
          <div className="absolute inset-0 z-20 flex pointer-events-none items-center justify-center">
            <TableOverlay
              title={`Failed to load ${scenarioLabel} data`}
              description={`Something went wrong while loading ${scenarioLabel.toLowerCase()} financials. Please try again.`}
              buttonText="Try Again"
              onClickButton={() => refetch()}
            />
          </div>
        ) : null}
        {showEmptyOverlay ? (
          <div className="absolute inset-0 z-20 flex pointer-events-none items-center justify-center">
            <TableOverlay
              title={emptyState.title}
              description={emptyState.description}
              buttonText="Refresh"
              onClickButton={() => refetch()}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
